import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { documentHtml } from './documentHtml';

export const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export default function DocxPreview({
  blob,
  title
}: {
  blob: Blob;
  title: string;
}) {
  const [preview, setPreview] = useState<{
    blob: Blob;
    html?: string;
    error?: boolean;
  } | null>(null);
  useEffect(() => {
    let active = true;
    let worker: Worker | undefined;
    const fail = () => {
      if (active) setPreview({ blob, error: true });
      active = false;
      window.clearTimeout(timeout);
      worker?.terminate();
    };
    const timeout = window.setTimeout(fail, 15000);
    const prepare = async () => {
      if (blob.size > 10 * 1024 * 1024) {
        fail();
      } else {
        try {
          const { createDocxWorker } = await import('./createDocxWorker');
          if (!active) return;
          worker = createDocxWorker();
          worker.onmessage = ({
            data
          }: MessageEvent<{ html?: string; error?: boolean }>) => {
            window.clearTimeout(timeout);
            if (active)
              setPreview({
                blob,
                ...(typeof data.html === 'string'
                  ? { html: documentHtml(data.html) }
                  : { error: true })
              });
            worker?.terminate();
          };
          worker.onerror = fail;
          blob
            .arrayBuffer()
            .then((bytes) => {
              if (active) worker?.postMessage(bytes, [bytes]);
            })
            .catch(fail);
        } catch {
          fail();
        }
      }
    };
    void prepare();
    return () => {
      active = false;
      window.clearTimeout(timeout);
      worker?.terminate();
    };
  }, [blob]);

  const current = preview?.blob === blob ? preview : null;
  if (current?.error)
    return (
      <Alert severity="warning">
        This Word document could not be previewed. Download the original file to
        open it in Word.
      </Alert>
    );
  if (!current?.html)
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ height: '100%' }}
        role="status"
      >
        <CircularProgress size={30} />
        <Typography>Preparing Word preview...</Typography>
      </Stack>
    );
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="caption" sx={{ px: 2, py: 1 }}>
        Word preview. Layout may differ from the original file.
      </Typography>
      <Box
        component="iframe"
        title={title}
        sandbox=""
        referrerPolicy="no-referrer"
        srcDoc={current.html}
        sx={{
          flex: 1,
          width: '100%',
          minHeight: 300,
          border: 0,
          bgcolor: 'white'
        }}
      />
    </Box>
  );
}
